const TRUSTED_AVATAR_REMOTE_HOST_PATTERNS = [
  /^iconai-[a-z0-9-]+\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i,
];
const ALLOWED_AVATAR_CONTENT_TYPES = new Set([
  'image/avif',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const MAX_AVATAR_PROXY_BYTES = 10 * 1024 * 1024;

type AvatarContentLengthValidationResult =
  | {
      isValid: true;
      contentLength: number;
    }
  | {
      isValid: false;
      reason: 'invalid' | 'missing' | 'too_large';
    };

/**
 * Checks whether a remote avatar image URL is safe to fetch through the local proxy.
 *
 * @param url The parsed remote image URL.
 * @returns `true` when the URL matches the allowlist for avatar image hosts.
 */
export function isTrustedAvatarRemoteUrl(url: URL) {
  return (
    url.protocol === 'https:' &&
    TRUSTED_AVATAR_REMOTE_HOST_PATTERNS.some((pattern) =>
      pattern.test(url.hostname)
    )
  );
}

/**
 * Resolves an image source suitable for client-side avatar face detection.
 *
 * Relative, blob, and data URLs are returned as-is. Trusted remote upload URLs
 * are rewritten through a same-origin proxy so browser vision APIs can read
 * their pixels safely.
 *
 * @param imageUrl The original avatar image URL.
 * @param currentOrigin The current page origin, when available.
 * @returns A safe image source for detection, or `null` when unsupported.
 */
export function getAvatarDetectionImageSource(
  imageUrl: string,
  currentOrigin?: string
) {
  if (!imageUrl) {
    return null;
  }

  if (
    imageUrl.startsWith('/') ||
    imageUrl.startsWith('blob:') ||
    imageUrl.startsWith('data:')
  ) {
    return imageUrl;
  }

  let parsedUrl: URL;

  try {
    parsedUrl = new URL(imageUrl);
  } catch {
    return null;
  }

  if (currentOrigin && parsedUrl.origin === currentOrigin) {
    return parsedUrl.toString();
  }

  if (!isTrustedAvatarRemoteUrl(parsedUrl)) {
    return null;
  }

  const params = new URLSearchParams({
    src: parsedUrl.toString(),
  });

  return `/api/avatar-image?${params.toString()}`;
}

/**
 * Validates and normalizes the upstream avatar response content type.
 *
 * Only explicitly allowlisted raster image media types are accepted. Response
 * parameters are stripped before validation so the proxy can forward a clean
 * media type to the browser.
 *
 * @param contentTypeHeader The upstream `Content-Type` header value.
 * @returns The normalized media type, or `null` when the type is not allowed.
 */
export function getSafeAvatarContentType(contentTypeHeader: string | null) {
  const normalizedContentType = contentTypeHeader
    ?.split(';', 1)[0]
    ?.trim()
    .toLowerCase();

  if (!normalizedContentType) {
    return null;
  }

  return ALLOWED_AVATAR_CONTENT_TYPES.has(normalizedContentType)
    ? normalizedContentType
    : null;
}

/**
 * Validates the upstream avatar `Content-Length` header before the proxy
 * streams any bytes to the browser.
 *
 * Trusted avatar responses are expected to provide a numeric content length.
 * The proxy rejects missing, malformed, and oversized values so it cannot be
 * used to relay arbitrarily large payloads from trusted remote hosts.
 *
 * @param contentLengthHeader The upstream `Content-Length` header value.
 * @param maxBytes The maximum allowed avatar payload size in bytes.
 * @returns The parsed content length when valid, otherwise the rejection reason.
 */
export function validateAvatarContentLength(
  contentLengthHeader: string | null,
  maxBytes: number = MAX_AVATAR_PROXY_BYTES
): AvatarContentLengthValidationResult {
  const normalizedContentLength = contentLengthHeader?.trim();

  if (!normalizedContentLength) {
    return {
      isValid: false,
      reason: 'missing',
    };
  }

  if (!/^\d+$/.test(normalizedContentLength)) {
    return {
      isValid: false,
      reason: 'invalid',
    };
  }

  const contentLength = Number.parseInt(normalizedContentLength, 10);

  if (!Number.isSafeInteger(contentLength) || contentLength <= 0) {
    return {
      isValid: false,
      reason: 'invalid',
    };
  }

  if (contentLength > maxBytes) {
    return {
      isValid: false,
      reason: 'too_large',
    };
  }

  return {
    isValid: true,
    contentLength,
  };
}
